import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { UpdateUserTeamDto } from 'api/Identity/Types/UpdateUserTeamDto'
import { UpsertUserUserTeams } from 'api/Identity/Types/UpsertUserUserTeams'
import { UserKindFilterKey, UserKindTypes } from 'api/Identity/Types/UserKindTypes'
import { UserStatusFilterKey, UserStatusTypes } from 'api/Identity/Types/UserStatusTypes'
import {
  UserTeamDto,
  UserTeamFilterKey,
  UserTeamKey,
  UserTeamSortKey,
} from 'api/Identity/Types/UserTeamDto'
import { PageData } from 'api/Pagination/Types'
import { AxiosError } from 'axios'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import { FilterCell } from 'components/Filter/FilterCell'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { calculatePageIndex } from 'features/DataGrid/helpers'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchClientLanguagesAction } from 'store/clients/clients.actions'
import { pageableSettings } from 'store/grid/grid.slice'
import { fetchTeamsAction } from 'store/identity/identity.actions'
import { instanceOfAxiosError, useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOneWithFetchingUpdate } from 'utils/api/useFetchOneWithFetchingUpdate'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { hasPermission } from 'utils/permissionsValidator'
import {
  buildQuery,
  buildSortQuery,
  FilterLogic,
  isFilterDescriptor,
  SortLogic,
} from 'utils/QueryHelpers'
import stringComparator from 'utils/stringComparator'

import { UpdateUserAutoAssignment } from '../../../api/Identity/Types/AddEditUser'
import {
  EditUserAutoAssignmentForm,
  EditUserAutoAssignmentFormValues,
} from '../Users/EditUserAutoAssignmentForm'
import AdministrationAddUserTeamForm from './AdministrationAddUserTeamForm'
import AdministrationUserTeamForm from './AdministrationEditUserTeamForm'
import administrationUserTeamsColumn from './administrationUserTeamsColumn'

interface AdministrationUsersTeamsProps {
  data: PageData<UserTeamDto[]>
  permissions: PermissionDto[]
  filter: CompositeFilterDescriptor
  pagination: Page
  onFilterChange(event: GridFilterChangeEvent): void
  onPageChange(event: GridPageChangeEvent): void
  onAdministrationUserAdd(userTeam: UserTeamDto): void
  onAdministrationUserEdit(userTeam: UserTeamDto): void
  onAdministrationUserRemove(userTeam: UserTeamDto): void
  setPauseAutoAssignmentModal(userTeam: UserTeamDto): void
}

const AdministrationUsersTeamsUI = (props: AdministrationUsersTeamsProps) => {
  const identityApiClient = useApiClient(IdentityApiClient)
  const setToast = useContext(ToastContext)

  const [deleteModal, setDeleteModal] = useState<{
    visible: boolean
    data?: undefined | UserTeamDto
  }>({
    visible: false,
  })

  const handleDeleteUserConfirmation = (e: UserTeamDto) => {
    setDeleteModal({ data: e, visible: true })
  }

  const handleAdministrationUserRemove = async () => {
    if (deleteModal.data) await identityApiClient.deleteUserTeam(deleteModal.data.id)
    setToast(successToast(`User deleted`))
    setDeleteModal({ visible: false, data: undefined })
  }

  const {
    data,
    permissions,
    filter,
    pagination,
    onPageChange,
    onFilterChange,
    onAdministrationUserAdd,
    onAdministrationUserEdit,
    setPauseAutoAssignmentModal,
  } = props

  return (
    <>
      <Grid
        filterable
        resizable
        data={data.items}
        filter={filter}
        take={pagination.take}
        skip={pagination.skip}
        total={data.totalResults}
        onPageChange={onPageChange}
        onFilterChange={onFilterChange}
        pageable={pageableSettings}
      >
        {administrationUserTeamsColumn
          .filter((c) => c.show !== false)
          .map((column, idx) => (
            <Column key={idx} {...column} />
          ))}
        <Column
          width={50}
          title=''
          filterCell={FilterCell}
          cell={(gridCellProps: GridCellProps) => (
            <td>
              <MoreButton
                items={[
                  {
                    name: 'Add',
                    'data-test': 'add',
                    disabled: !hasPermission(permissions, PermissionsEnum.CanEditUserTeams),
                    onClick: () => onAdministrationUserAdd(gridCellProps.dataItem),
                  },
                  {
                    name: 'Edit',
                    'data-test': 'edit',
                    disabled:
                      !gridCellProps.dataItem.team.tickmillCompany ||
                      !hasPermission(permissions, PermissionsEnum.CanEditUserTeams),
                    onClick: () => onAdministrationUserEdit(gridCellProps.dataItem),
                  },
                  {
                    name: 'Remove',
                    'data-test': 'remove',
                    disabled:
                      !gridCellProps.dataItem.team.tickmillCompany ||
                      !hasPermission(permissions, PermissionsEnum.CanRemoveUserTeams),
                    onClick: () =>
                      handleDeleteUserConfirmation(gridCellProps.dataItem as UserTeamDto),
                  },
                  {
                    name: 'Paused Auto Assignment',
                    'data-test': 'pause_auto_assignment',
                    disabled: !hasPermission(
                      permissions,
                      PermissionsEnum.CanEditPausedAutoassignments
                    ),
                    onClick: () => setPauseAutoAssignmentModal(gridCellProps.dataItem),
                  },
                ]}
                data-test='more-button'
              />
            </td>
          )}
        />
      </Grid>
      <ConfirmationModal
        contentText={`Are you sure you want to delete team user?`}
        yesButtonText='Yes, delete'
        onYes={handleAdministrationUserRemove}
        onCancel={() => setDeleteModal({ data: undefined, visible: false })}
        visible={!!deleteModal.visible}
      />
    </>
  )
}

const AdministrationUserTeams = (): JSX.Element => {
  const dispatch = useAppDispatch()

  const [auth] = useContext(AuthSessionContext)

  const identityApiClient = useApiClient(IdentityApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)
  const [autoAssignmentModal, setAutoAssignmentModal] = useState<{
    visible: boolean
    data?: UserTeamDto
  }>({
    visible: false,
  })

  const [isEditUserTeamModalOpen, setEditUserTeamModalOpen] = useState<{
    data?: UserTeamDto
    visible: boolean
  }>({
    data: undefined,
    visible: false,
  })
  const [isForceModalVisible, setForceModalVisible] = useState(false)
  const [submitValues, setSubmitValues] = useState<UpsertUserUserTeams>()
  const [isAddUserTeamModalOpen, setAddUserTeamModalOpen] = useState<{
    data?: UserTeamDto
    visible: boolean
  }>({
    data: undefined,
    visible: false,
  })

  const roles = useAppSelector((state) => state.identity.roles)
  const dateFormat = useAppSelector((state) => state.identity.dateFormat)

  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())
  const permissions = auth?.permissions || []

  const teams = useAppSelector((state) => state.identity.teams) ?? []
  const languages = useAppSelector((state) => state.clients.language) ?? []

  const sortedLanguages = useMemo(
    () => [...languages].sort((a, b) => stringComparator(a.name, b.name)),
    [languages]
  )
  const { callback, forceRefresh } = useCallbackWithForceRefresh(
    () =>
      identityApiClient.getUserTeams({
        search: getSearchQuery(),
        sort: getSortQuery(),
        pageIndex: calculatePageIndex(paginationState.skip, paginationState.take),
        pageSize: paginationState.take,
      }),
    [paginationState] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const { data, isLoading } = useFetchOneWithFetchingUpdate(callback)

  useEffect(() => {
    if (isEmpty(teams)) {
      dispatch(fetchTeamsAction(identityApiClient))
    }
    if (isEmpty(languages)) {
      dispatch(fetchClientLanguagesAction(clientsApiClient))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isLoading) {
        setPaginationState((page) => ({ ...page, skip: 0 }))
      }
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [filter.filters]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = async (event: GridFilterChangeEvent) => {
    setFilter(event.filter)
  }

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPaginationState(event.page)
  }

  const handleAdministrationUserEdit = async (values: UpdateUserTeamDto) => {
    await identityApiClient.updateUserTeam(values)
    forceRefresh()
    setEditUserTeamModalOpen({ data: undefined, visible: false })
  }

  const handleAdministrationUserAdd = async (
    values: UpsertUserUserTeams,
    overrideRegionCheck?: boolean
  ) => {
    try {
      if (isAddUserTeamModalOpen.data?.user?.id)
        await identityApiClient.addUserTeams(
          isAddUserTeamModalOpen.data.user.id,
          values,
          overrideRegionCheck ?? false
        )
      forceRefresh()
      setAddUserTeamModalOpen({ data: undefined, visible: false })
      setSubmitValues(undefined)
      setForceModalVisible(false)
    } catch (err: unknown) {
      const error = err as { response: AxiosError<{ code?: string }> }
      if (instanceOfAxiosError(error?.response)) {
        const errorCode = error.response.response?.data?.code
        if (errorCode === 'same_region_team') {
          setSubmitValues(values)
          setForceModalVisible(true)
          return
        }
      }
      throw err
    }
  }

  const handleAdministrationUserAutoAssignmentEdit = async (
    values: EditUserAutoAssignmentFormValues
  ) => {
    if (autoAssignmentModal.data) {
      const currentUser = autoAssignmentModal.data.user
      const updateUserRequest: UpdateUserAutoAssignment = {
        autoAssignmentPausedStart: values.autoAssignmentPausedStart,
        autoAssignmentPausedUntil: values.autoAssignmentPausedUntil,
        isAutoAssignmentPaused: values.isAutoAssignmentPaused,
      }
      await identityApiClient.updateUserAutoAssignment(currentUser.id, updateUserRequest)
      setAutoAssignmentModal({ visible: false, data: undefined })
      forceRefresh()
    }
  }

  const handleAdministrationUserRemove = async (userTeam: UserTeamDto) => {
    await identityApiClient.deleteUserTeam(userTeam.id)
    forceRefresh()
  }

  const handleEditUserTeamModalOpen = (userTeam: UserTeamDto) => {
    setEditUserTeamModalOpen({ data: userTeam, visible: true })
  }

  const handleEditUserTeamModalClose = () => {
    setEditUserTeamModalOpen({ data: undefined, visible: false })
  }

  const handleAddUserTeamModalOpen = async (userTeam: UserTeamDto) => {
    const userData = await identityApiClient.getUserData(userTeam.user.id)
    setAddUserTeamModalOpen({ data: { ...userTeam, userData }, visible: true })
  }

  const handleAddUserTeamModalClose = () => {
    setAddUserTeamModalOpen({ data: undefined, visible: false })
    setSubmitValues(undefined)
    setForceModalVisible(false)
  }

  const setPauseAutoAssignmentModal = (teamDto: UserTeamDto) => {
    setAutoAssignmentModal({ data: teamDto, visible: true })
  }

  const getSearchQuery = useCallback(
    () => {
      const filterSearchQuery = normalizeUserTeamsSearchQuery(filter)
      return (
        buildQuery({
          logic: FilterLogic.AND,
          filters: filterSearchQuery.filters,
          defaultFilters: {
            logic: FilterLogic.AND,
            filters: [
              {
                field: UserStatusFilterKey.userStatusId,
                operator: '==',
                logic: FilterLogic.AND,
                value: UserStatusTypes.Confirmed,
              },
              {
                field: UserKindFilterKey.userKindId,
                operator: '==',
                logic: FilterLogic.AND,
                value: UserKindTypes.User,
              },
            ],
          },
        }) ?? null
      )
    },
    [filter.filters] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const getSortQuery = useCallback((): string | null => {
    return buildSortQuery([
      {
        field: UserTeamSortKey.user,
        dir: SortLogic.ASC,
      },
    ])
  }, [])

  return (
    <React.Fragment>
      {autoAssignmentModal.data && autoAssignmentModal.visible && (
        <EditUserAutoAssignmentForm
          isOpen={autoAssignmentModal.visible}
          onClose={() => setAutoAssignmentModal({ visible: false, data: undefined })}
          user={autoAssignmentModal.data.user}
          roles={roles}
          dateFormat={dateFormat}
          onSubmit={handleAdministrationUserAutoAssignmentEdit}
        />
      )}
      {isForceModalVisible && (
        <ConfirmationModal
          contentText={`The user already has visibility to this country. Do you still want to proceed?`}
          yesButtonText='Yes'
          onYes={() => submitValues && handleAdministrationUserAdd(submitValues, true)}
          onCancel={() => {
            setForceModalVisible(false)
          }}
          visible={isForceModalVisible}
        />
      )}
      {isEditUserTeamModalOpen.data && (
        <AdministrationUserTeamForm
          isOpen={isEditUserTeamModalOpen.visible}
          onClose={handleEditUserTeamModalClose}
          data={isEditUserTeamModalOpen.data}
          languages={sortedLanguages}
          onSubmit={handleAdministrationUserEdit}
        />
      )}
      {isAddUserTeamModalOpen.data && !isForceModalVisible && (
        <Modal
          width={600}
          visible={isAddUserTeamModalOpen.visible && !isForceModalVisible}
          onClose={handleAddUserTeamModalClose}
        >
          <AdministrationAddUserTeamForm
            teams={teams.filter((team) => team.tickmillCompany?.id)}
            values={submitValues}
            data={isAddUserTeamModalOpen.data}
            onSubmit={handleAdministrationUserAdd}
          />
        </Modal>
      )}
      <Breadcrumbs crumbs={[{ title: 'User Teams', link: links.administrationUserTeams.url }]} />
      <Loading isLoading={isLoading}>
        {data && (
          <AdministrationUsersTeamsUI
            data={data}
            permissions={permissions}
            filter={filter}
            pagination={paginationState}
            onFilterChange={handleFilterChange}
            onPageChange={handlePageChange}
            onAdministrationUserAdd={handleAddUserTeamModalOpen}
            onAdministrationUserEdit={handleEditUserTeamModalOpen}
            onAdministrationUserRemove={handleAdministrationUserRemove}
            setPauseAutoAssignmentModal={setPauseAutoAssignmentModal}
          />
        )}
      </Loading>
    </React.Fragment>
  )
}

const normalizeUserTeamsSearchQuery = (filterDescriptor: CompositeFilterDescriptor) => {
  const filters = filterDescriptor.filters
    .filter((filterItem: FilterDescriptor | CompositeFilterDescriptor) => {
      if (isFilterDescriptor(filterItem)) {
        return !!filterItem.value
      }
      return false
    })
    .map((filterItem) => {
      if (isFilterDescriptor(filterItem)) {
        if (filterItem.field === UserTeamKey.user) {
          return {
            ...filterItem,
            field: UserTeamFilterKey.user,
            operator: 'like',
            value: filterItem?.value,
          }
        }
        if (filterItem.field === UserTeamKey.team) {
          return {
            ...filterItem,
            field: UserTeamFilterKey.team,
            operator: '==',
            value: filterItem?.value,
          }
        }
        if (filterItem.field === UserTeamKey.autoAssignmentEnabled) {
          return {
            ...filterItem,
            field: UserTeamFilterKey.autoAssignmentEnabled,
            operator: '==',
            value: filterItem?.value,
          }
        }
        if (filterItem.field === UserTeamKey.visibility) {
          return {
            ...filterItem,
            field: UserTeamFilterKey.visibility,
            operator: '==',
            value: filterItem?.value,
          }
        }
        if (filterItem.field === UserTeamKey.language) {
          return {
            ...filterItem,
            field: UserTeamFilterKey.language,
            operator: '==',
            value: filterItem?.value,
          }
        }
      }
      return filterItem
    })

  return { ...filterDescriptor, filters }
}

export default AdministrationUserTeams
